import { useEffect, useState, useMemo, useRef } from "react";
import SweetAlert2 from "react-sweetalert2";
import axios from "axios";
import { Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import GeneralTab from "./components/GeneralTab";
import AccessControlTab from "../shared/AccessControlTab";

const Settings = () => {
  const [subaccountDetails, setSubaccountDetails] = useState<any>(null);
  const [merchantDetails, setMerchantDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [swalProps, setSwalProps] = useState<any>({});
  const merchantid = localStorage.getItem("merchantId");
  const subaccountid = localStorage.getItem("subaccountId");

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  const fetchSubaccountDetails = async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountid}`,
        { signal }
      );

      const data = response.data.subaccount?.payload;

      setSubaccountDetails(data);
      setIsLoading(false);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching data", error);
    }
  };

  const fetchMerchantDetail = async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/merchant/get-merchant-by-id?merchantid=${merchantid}`,
        { signal }
      );

      let merchantData = response.data.merchant?.payload;
      setMerchantDetails(merchantData);
      setIsLoading(false);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      setIsLoading(false);
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const getData = async () => {
      await fetchSubaccountDetails(signal);
      if (signal.aborted) return;
      await fetchMerchantDetail(signal);
    };
    getData();
  }, []); // eslint-disable-line

  // Determine data source for Access Control
  // If subaccount has its own auth keys, use subaccount data; otherwise use merchant data
  const subaccountHasAuth = subaccountDetails?.auth1 && subaccountDetails?.auth2;
  const accessControlSource = subaccountHasAuth ? subaccountDetails : merchantDetails;
  const accessControlFetch = subaccountHasAuth ? fetchSubaccountDetails : fetchMerchantDetail;

  // Define tab configuration
  const tabConfig = useMemo(() => [
    { label: "General", show: true },
    { label: "API Key", show: true },
  ], []);

  // Filter visible tabs (currently all are visible, but ready for permissions)
  const visibleTabs = useMemo(() => {
    return tabConfig.filter(tab => tab.show);
  }, [tabConfig]);

  // Render tab content based on label
  const renderTabContent = (label: string) => {
    switch (label) {
      case "General":
        return (
          <GeneralTab
            data={subaccountDetails}
            setSubaccountDetails={setSubaccountDetails}
            setSwalProps={setSwalProps}
            isOtherTabUpdating={isUpdating}
            setIsUpdating={setIsUpdating}
            fetchSubaccountDetails={fetchSubaccountDetails}
            isLoading={isLoading}
          />
        );
      case "API Key":
        return (
          <AccessControlTab
            data={accessControlSource}
            mode={subaccountHasAuth ? "subaccount" : "merchant"}
            is_active={accessControlSource?.is_active}
            auth1={accessControlSource?.auth1}
            auth2={accessControlSource?.auth2}
            isProcessing={isUpdating || isLoading}
            setSwalProps={setSwalProps}
            fetchDetail={accessControlFetch}
            isOtherTabUpdating={isUpdating}
            setIsUpdating={setIsUpdating}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Tabs className="card inter mt-3 !rounded-xl bg-white text-gray-800 shadow-sm ring-1 ring-gray-100">
        <TabList className="rounded-tl-custom rounded-tr-custom scrollbar-hide flex flex-wrap overflow-x-auto sm:flex-nowrap">
          {visibleTabs.map((tab, index) => (
            <Tab
              key={index}
              className={`child-tab rounded-tl-custom rounded-tr-custom sm:text-md whitespace-nowrap bg-white px-4 py-3 pt-4 text-sm text-navy-700 sm:px-6 sm:py-4 sm:pt-6 ${
                isLoading || isUpdating || accessControlSource === null
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels className="rounded-bl-custom rounded-br-custom border-t border-gray-200 bg-white">
          {visibleTabs.map((tab, index) => (
            <TabPanel key={index}>
              {renderTabContent(tab.label)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default Settings;
